import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { ModalProvider, useModal, EarlyAccessForm, ConnectAccountPanel } from './ui'

function Brand() {
  return (
    <Link to="/" className="brand" aria-label="NikaQuant Atlas home">
      <span className="brand-mark" aria-hidden />
      <span className="brand-text">
        <strong>Atlas</strong>
        <span>NikaQuant</span>
      </span>
    </Link>
  )
}

const LINKS = [
  { to: '/dashboard', label: 'Track' },
  { to: '/proof/grok-alpha', label: 'Proof' },
  { to: '/risk', label: 'Risk OS' },
  { to: '/journal', label: 'Journal' },
  { to: '/pricing', label: 'Pricing' },
]

function NavBar() {
  const [open, setOpen] = useState(false)
  const loc = useLocation()
  const modal = useModal()

  useEffect(() => {
    setOpen(false)
  }, [loc.pathname])

  return (
    <>
      <header className="nav">
        <div className="container nav-inner">
          <Brand />
          <nav className="nav-links" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === '/'}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="nav-cta">
            <button
              type="button"
              className="btn btn-ghost btn-sm hide-mobile"
              onClick={() =>
                modal.open(<ConnectAccountPanel onClose={modal.close} />, { title: 'Connect account' })
              }
            >
              Connect
            </button>
            <Link to="/dashboard" className="btn btn-primary btn-sm hide-mobile">
              Open demo
            </Link>
            <button
              type="button"
              className="nav-toggle"
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((v) => !v)}
            >
              ☰
            </button>
          </div>
        </div>
      </header>
      <div id="mobile-nav" className={`mobile-nav ${open ? 'open' : ''}`}>
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to}>
            {l.label}
          </NavLink>
        ))}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => modal.open(<ConnectAccountPanel onClose={modal.close} />, { title: 'Connect account' })}
        >
          Connect account
        </button>
        <Link to="/dashboard" className="btn btn-primary">
          Open demo tracker
        </Link>
      </div>
    </>
  )
}

function Footer() {
  const modal = useModal()
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div>
          <Brand />
          <p className="muted" style={{ marginTop: '0.9rem', maxWidth: 320 }}>
            Track every account. Prove every result. Control every risk. Verified performance intelligence for
            MetaTrader operators.
          </p>
          <p className="dev-label">Grok development experiment.</p>
        </div>
        <div>
          <h4>Product</h4>
          <Link to="/dashboard">Atlas Track</Link>
          <Link to="/proof/grok-alpha">Atlas Proof</Link>
          <Link to="/risk">Risk OS</Link>
          <Link to="/journal">Journal Lab</Link>
          <Link to="/pricing">Pricing</Link>
        </div>
        <div>
          <h4>Concepts</h4>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '0.28rem 0', justifyContent: 'flex-start' }}
            onClick={() =>
              modal.open(<ConnectAccountPanel onClose={modal.close} />, { title: 'Connection methods' })
            }
          >
            Connection methods
          </button>
          <a href="/#verification">Verification</a>
          <a href="/#security">Security boundaries</a>
          <a href="/#faq">FAQ</a>
        </div>
        <div>
          <h4>Access</h4>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '0.28rem 0', justifyContent: 'flex-start' }}
            onClick={() => modal.open(<EarlyAccessForm />, { title: 'Early access' })}
          >
            Early access
          </button>
          <Link to="/dashboard">Live demo data</Link>
          <a href="https://nikaquant.com" rel="noreferrer" target="_blank">
            NikaQuant.com
          </a>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>© {new Date().getFullYear()} NikaQuant Atlas · fictional demo data · not financial advice</span>
        <span className="dev-label">grok-dev.tracker.nikaquant.com</span>
      </div>
    </footer>
  )
}

export function AppLayout() {
  return (
    <ModalProvider>
      <div className="app-shell">
        <NavBar />
        <main className="main">
          <Outlet />
        </main>
        <Footer />
      </div>
    </ModalProvider>
  )
}

export function Page({
  children,
  wide,
}: {
  children: ReactNode
  wide?: boolean
}) {
  return <div className={wide ? 'container-wide app-page' : 'container app-page'}>{children}</div>
}
